import {
  ActionIcon,
  Autocomplete,
  Button,
  Card,
  Checkbox,
  Divider,
  Drawer,
  InputLabel,
  LoadingOverlay,
  Modal,
  MultiSelect,
  NumberInput,
  Pagination,
  PasswordInput,
  Radio,
  Select,
  TextInput,
  Textarea,
  createTheme,
  rem,
  rgba,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type MantineTheme,
} from "@mantine/core";
import { TimeInput, DatePickerInput, DateTimePicker } from "@mantine/dates";
import { branding } from "./config";

const fontStack =
  'Inter, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif';

const primaryColorName = branding.primaryColorName;
const brand = branding.palette;
const brandingSettings = {
  name: branding.themeName,
  primaryColor: primaryColorName,
  palette: brand,
  gradient: branding.gradient,
};

const shadowTokens = {
  xs: "0 1px 2px rgba(15, 23, 42, 0.08)",
  sm: "0 1px 4px rgba(15, 23, 42, 0.12)",
  md: "0 2px 10px rgba(15, 23, 42, 0.16)",
};

const success = [
  "#ebfbee",
  "#d3f9d8",
  "#b2f2bb",
  "#8ce99a",
  "#69db7c",
  "#51cf66",
  "#40c057",
  "#37b24d",
  "#2b8a3e",
  "#237032",
] as const satisfies MantineColorsTuple;

const warning = [
  "#fff9db",
  "#fff3bf",
  "#ffec99",
  "#ffe066",
  "#ffd43b",
  "#fcc419",
  "#fab005",
  "#f59f00",
  "#e67700",
  "#d9480f",
] as const satisfies MantineColorsTuple;

const danger = [
  "#fff5f5",
  "#ffe3e3",
  "#ffc9c9",
  "#ffa8a8",
  "#ff8787",
  "#ff6b6b",
  "#fa5252",
  "#f03e3e",
  "#e03131",
  "#c92a2a",
] as const satisfies MantineColorsTuple;

const neutral = [
  "#f8fafc",
  "#f1f5f9",
  "#e2e8f0",
  "#cbd5f5",
  "#94a3b8",
  "#64748b",
  "#475569",
  "#334155",
  "#1e293b",
  "#0f172a",
] as const satisfies MantineColorsTuple;

const treeTokens = {
  indent: 18,
  nodeMinHeight: 50,
  typeIconMarginTop: 12,
  caretSpacing: 18,
  caretOffsetClosed: 4,
  caretOffsetOpen: 10,
  leafLabelPull: 20,
  actionVariant: "light" as const,
  actionColors: {
    add: primaryColorName,
    edit: primaryColorName,
    copy: "neutral",
    delete: "danger",
  },
  selectedBg: { color: primaryColorName, shade: 1 as const },
};

const layoutTokens = {
  pagePadding: {
    base: rem(16),
    md: rem(24),
    lg: rem(32),
  },
  contentMaxWidth: rem(1280),
  sectionGap: rem(24),
  cardGap: rem(20),
};

const formTokens = {
  maxWidth: rem(1280),
  fieldGap: rem(16),
  actions: {
    gap: rem(12),
    marginTop: rem(24),
    paddingX: rem(16),
    paddingY: rem(12),
    dividerSpacing: rem(16),
    dividerColorShade: 2,
    withDivider: true,
    justify: "space-between" as const,
    sticky: true,
    stickyOffset: rem(0),
    withBorder: true,
    radius: "md" as const,
    shadow: "xs" as const,
    // Use CSS variable so actions bar adapts to light/dark mode
    background: "var(--app-surface)",
    cancelColor: "gray" as const,
    cancelVariant: "light" as const,
  },
};

declare module "@mantine/core" {
  export interface MantineThemeOther {
    tree: typeof treeTokens;
    layout: typeof layoutTokens;
    form: typeof formTokens;
    branding: typeof brandingSettings;
  }
}

const labelStyles = (theme: MantineTheme) => ({
  color: 'var(--app-text)',
  fontWeight: 600,
  fontSize: theme.fontSizes.sm,
  marginBottom: rem(4),
});



export const appTheme = createTheme({
  fontFamily: fontStack,
  headings: {
    fontFamily: fontStack,
    fontWeight: "600",
  },
  primaryColor: primaryColorName,
  primaryShade: { light: 5, dark: 4 },
  shadows: shadowTokens,
  radius: {
    xs: rem(4),
    sm: rem(6),
    md: rem(8),
    lg: rem(12),
    xl: rem(16),
  },
  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(16),
    lg: rem(20),
    xl: rem(28),
  },
  fontSizes: {
    xs: rem(12),
    sm: rem(13),
    md: rem(15),
    lg: rem(18),
    xl: rem(22),
  },
  defaultRadius: "sm",
  colors: {
    [primaryColorName]: brand,
    success,
    warning,
    danger,
    neutral,
  },
  defaultGradient: branding.gradient,
  components: {
    TextInput: TextInput.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    Select: Select.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
        option: {
          color: 'var(--app-text)',
          "&:hover": {
            backgroundColor: 'var(--app-surface-muted)',
          },
          "&[dataSelected]": {
            backgroundColor: rgba(theme.colors[theme.primaryColor][6], 0.1),
            color: theme.colors[theme.primaryColor][7],
          },
        },
      }),
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
        pill: {
          backgroundColor: theme.colors[theme.primaryColor][0],
          color: theme.colors[theme.primaryColor][7],
        },
      }),
    }),
    NumberInput: NumberInput.extend({
      defaultProps: { size: "sm", withKeyboardEvents: false },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    Textarea: Textarea.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
        input: {
          minHeight: rem(90),
        },
      }),
    }),
    Button: Button.extend({
      defaultProps: { size: "sm", radius: "md" },
      styles: () => ({
        root: {
          fontWeight: 600,
        },
      }),
    }),
    Radio: Radio.extend({
      defaultProps: { size: "sm", color: primaryColorName },
      styles: (theme) => ({
        label: {
          color: theme.colors.gray[7],
          fontWeight: 500,
          fontSize: theme.fontSizes.sm,
        },
      }),
    }),
    Checkbox: Checkbox.extend({
      defaultProps: { size: "sm", color: primaryColorName },
      styles: (theme) => ({
        label: {
          color: theme.colors.gray[7],
          fontWeight: 500,
          fontSize: theme.fontSizes.sm,
        },
        input: {
          borderRadius: '50%',
        },
      }),
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    TimeInput: TimeInput.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    DateTimePicker: DateTimePicker.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    InputLabel: InputLabel.extend({
      defaultProps: { size: "sm" },
      styles: (theme) => ({
        label: labelStyles(theme),
      }),
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        size: "sm",
        variant: "subtle",
        color: "neutral",
        radius: "md",
      },
      styles: (theme) => ({
        root: {
          borderRadius: theme.radius.md,
          transition: "background-color 120ms ease",
        },
      }),
    }),
    LoadingOverlay: LoadingOverlay.extend({
      defaultProps: {
        overlayProps: { radius: "sm", blur: 2 },
        loaderProps: { color: primaryColorName, type: "bars" },
      },
      styles: (theme) => ({
        overlay: {
          backgroundColor: rgba(theme.colors.neutral[9], 0.2),
        },
      }),
    }),
    Card: Card.extend({
      defaultProps: {
        withBorder: true,
        shadow: "xs",
        padding: "lg",
        radius: "md",
      },
    }),
    Drawer: Drawer.extend({
      defaultProps: {
        padding: "lg",
        radius: "md",
        overlayProps: { opacity: 0.35, blur: 4 },
      },
      // Surfaces and borders must come from the scheme-aware tokens: theme.white
      // and theme.colors.gray[n] are single values, so they stayed light in dark
      // mode and painted every drawer white.
      styles: {
        header: {
          borderBottom: "1px solid var(--app-border)",
          marginBottom: rem(12),
          paddingBottom: rem(12),
          backgroundColor: "var(--app-surface)",
          color: "var(--app-text)",
        },
        content: {
          backgroundColor: "var(--app-surface)",
          color: "var(--app-text)",
        },
        body: {
          backgroundColor: "var(--app-surface)",
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        padding: "lg",
        radius: "md",
        centered: true,
        overlayProps: { opacity: 0.4, blur: 6 },
      },
      styles: {
        header: {
          borderBottom: "1px solid var(--app-border)",
          marginBottom: rem(8),
          paddingBottom: rem(12),
        },
        body: {
          paddingTop: rem(8),
        },
      },
    }),
    Pagination: Pagination.extend({
      defaultProps: { size: "sm", radius: "md" },
    }),
    Divider: Divider.extend({
      styles: () => ({
        root: {
          borderColor: "var(--app-border)",
        },
      }),
    }),
  },
  other: {
    tree: treeTokens,
    layout: layoutTokens,
    form: formTokens,
    branding: brandingSettings,
  },
});

export const appCssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {},
  light: {
    "--app-primary": theme.colors[theme.primaryColor][5],
    "--app-primary-soft": `color-mix(in srgb, ${theme.colors[theme.primaryColor][5]} 10%, transparent)`,
    "--app-primary-soft-strong": `color-mix(in srgb, ${theme.colors[theme.primaryColor][5]} 20%, transparent)`,
    "--app-chip-active-bg": `color-mix(in srgb, ${theme.colors[theme.primaryColor][5]} 10%, transparent)`,
    "--app-chip-active-border": `color-mix(in srgb, ${theme.colors[theme.primaryColor][5]} 45%, transparent)`,
    "--app-chip-active-text": theme.colors[theme.primaryColor][8],
  },
  dark: {
    "--app-primary": theme.colors[theme.primaryColor][4],
    "--app-primary-soft": `color-mix(in srgb, ${theme.colors[theme.primaryColor][4]} 12%, transparent)`,
    "--app-primary-soft-strong": `color-mix(in srgb, ${theme.colors[theme.primaryColor][4]} 20%, transparent)`,
    "--app-chip-active-bg": `color-mix(in srgb, ${theme.colors[theme.primaryColor][4]} 14%, transparent)`,
    "--app-chip-active-border": `color-mix(in srgb, ${theme.colors[theme.primaryColor][4]} 40%, transparent)`,
    "--app-chip-active-text": theme.colors[theme.primaryColor][1],
  },
});

